<main>
  <loading-overlay [isLoading]="isLoading() > 0 || !enumsReady()" />
  <p-toast position="top-center" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container row-between-center gap-[1rem]">
        <h1>{{ 'JournalEntries_Title' | translateWF: 'Journal Entries' }}</h1>
        <!-- #5077 — the one way an entry is written by hand rather than by a document posting. -->
        <p-button [label]="'JournalEntry_Manual_New' | translateWF: 'New journal entry'" icon="pi pi-plus" (click)="createManualEntry()" />
      </div>
      <p class="page__subtitle">
        {{
          'JournalEntries_Subtitle'
            | translateWF
              : 'GL postings — most are generated automatically when a document is
        posted.'
        }}
      </p>
    </div>

    @if (summary(); as s) {
      <div class="cs-summary-grid">
        <p-card data-variant="summary" [header]="'JournalEntries_PostedEntries' | translateWF: 'Posted entries'">
          <ng-template pTemplate="subtitle">{{ s.postedCount | number }}</ng-template>
          {{ 'JournalEntries_InThisCompany' | translateWF: 'in this company' }}
        </p-card>
        <p-card data-variant="summary" data-severity="warning" [header]="'Draft' | translateWF: 'Draft'">
          <ng-template pTemplate="subtitle">{{ s.draftCount | number }}</ng-template>
          {{ 'JournalEntries_AwaitingPosting' | translateWF: 'awaiting posting' }}
        </p-card>
        <p-card data-variant="summary" data-severity="danger" [header]="'JournalEntries_Reversed' | translateWF: 'Reversed'">
          <ng-template pTemplate="subtitle">{{ s.reversedCount | number }}</ng-template>
          {{ 'JournalEntries_Entries' | translateWF: 'entries' }}
        </p-card>
        <p-card data-variant="summary" [header]="'JournalEntries_PostedVolume' | translateWF: 'Posted volume'">
          <ng-template pTemplate="subtitle">{{ s.postedVolume | number: '1.2-2' }} {{ summaryCurrency() }}</ng-template>
          {{ 'JournalEntries_FunctionalCurrency' | translateWF: 'functional currency' }}
        </p-card>
      </div>
    }

    <div class="cs-page-list-grid">
      @if (filterConfig() && showFilters()) {
        <div>
          <csl-dynamic-filters [filterConfig]="filterConfig()!" [state]="filterState" />
        </div>
      }

      <p-panel [header]="'JournalEntries_Title' | translateWF: 'Journal Entries'">
        <ng-template pTemplate="icons">
          <p-button
            [label]="(showFilters() ? 'Common_Hide_Filters' : 'Common_Show_Filters') | translateWF: (showFilters() ? 'Hide Filters' : 'Show Filters')"
            [icon]="showFilters() ? 'pi pi-filter-slash' : 'pi pi-filter'"
            (click)="showFilters.set(!showFilters())"
            variant="outlined"
            size="small"
          />
        </ng-template>
        <p-card data-variant="compact">
          <!-- Sorted server-side: the table holds one page, so sorting it in the browser would only reorder
               the 25 rows in hand and call it the ledger's order. Period has no column of its own to sort by
               — it is a year+period pair rendered together — so it follows the entry number, which runs in
               the same direction. -->
          <!-- The initial sort is bound as constants, not from the signals: the table only needs to be told
               once which header to mark, and re-binding it on every change would have the table emit a sort
               back at the handler that just set it. -->
          <p-table [value]="entries()" selectionMode="single" [lazy]="true" [sortField]="'number'" [sortOrder]="-1" (onSort)="onSort($event)">
            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="number">{{ 'JournalEntry_Number' | translateWF: 'Entry #' }}<p-sortIcon field="number" /></th>
                <th pSortableColumn="postingDate">{{ 'JournalEntry_PostingDate' | translateWF: 'Date' }}<p-sortIcon field="postingDate" /></th>
                <th>{{ 'JournalEntry_Period' | translateWF: 'Period' }}</th>
                <th pSortableColumn="reference">
                  {{
                    'JournalEntry_DescriptionReference'
                      | translateWF
                        : 'Description /
                Reference'
                  }}<p-sortIcon field="reference" />
                </th>
                <th pSortableColumn="sourceModule">{{ 'JournalEntry_Source' | translateWF: 'Source' }}<p-sortIcon field="sourceModule" /></th>
                <th class="text-right" pSortableColumn="totalFunctionalDebits">{{ 'JournalEntry_Total' | translateWF: 'Total' }}<p-sortIcon field="totalFunctionalDebits" /></th>
                <th pSortableColumn="status">{{ 'Status' | translateWF: 'Status' }}<p-sortIcon field="status" /></th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-rowData>
              @let entry = getRowData(rowData);
              <!-- The whole row opens the entry: there is exactly one thing to do with a posting, and every
                   other cell is read-only, so a single link would just make the target smaller. -->
              <tr class="cursor-pointer" [title]="'JournalEntry_Open' | translateWF: 'Open this entry'" (click)="redirectToDetails(entry.id)">
                <td class="text-primary font-medium">{{ entry.number }}</td>
                <td>{{ entry.postingDate | date: 'dd/MM/yyyy' }}</td>
                <td class="tabular-nums">{{ periodLabel(entry) }}</td>
                <td>
                  <!-- Description leads; the source document's own number sits under it, since that is what a
                       reconciler holding the paper actually searches for. -->
                  <div class="je-list__description">{{ entry.description }}</div>
                  @if (entry.reference) {
                    <div class="je-list__reference">{{ entry.reference }}</div>
                  }
                </td>
                <td>
                  <p-tag severity="info" [rounded]="true" [value]="sourceModuleLabel(entry.sourceModule)" />
                </td>
                <!-- Debits, not debits + credits: a balanced entry would otherwise read as twice its value.
                     Always in the BOOKS' currency, so a column of totals is a column of comparable numbers —
                     it used to render each entry in its own, which put 26,000 Lempiras above 2,000 dollars
                     and made the column impossible to scan or to sort. The entry's own figure follows it
                     where the two differ, as "books / transaction". -->
                <td class="text-right tabular-nums">
                  {{ entry.totalFunctionalDebits | number: '1.2-2' }} {{ currencyLabel(entry.functionalCurrency) }}
                  @if (entry.currency !== entry.functionalCurrency) {
                    <span class="je-list__transaction-total">/ {{ entry.totalDebits | number: '1.2-2' }} {{ currencyLabel(entry.currency) }}</span>
                  }
                </td>
                <td>
                  <p-tag [severity]="statusSeverity(entry.status)" [value]="statusLabel(entry.status)" />
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="7">{{ 'JournalEntries_NotFound' | translateWF: 'No journal entries found' }}</td>
              </tr>
            </ng-template>
          </p-table>

          <p-paginator
            [rows]="pageSize()"
            [totalRecords]="totalRecords()"
            [first]="(currentPage() - 1) * pageSize()"
            (onPageChange)="onPageChange($event)"
            [rowsPerPageOptions]="[25, 50, 100]"
            class="mt-4"
          />
        </p-card>
      </p-panel>
    </div>
  </div>
</main>
